import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/lib/theme.js';
import { useAuth, isAdmin, type CurrentUser } from '@/lib/auth.js';
import { Logo } from './Logo.js';
import { MobileDrawer, type MobileDrawerGroup } from './app/MobileDrawer.js';
import { MenuIcon, SunIcon, MoonIcon, ChevronDownIcon } from './icons/index.js';

const DRAWER_GROUPS: MobileDrawerGroup[] = [
  {
    label: 'Daily',
    items: [
      { to: '/today', label: 'Today' },
      { to: '/prayers', label: 'Prayers' },
      { to: '/fasting', label: 'Fasting' },
      { to: '/readings', label: 'Readings' },
      { to: '/reading-plans', label: 'Reading plans' },
    ],
  },
  {
    label: 'Reference',
    items: [
      { to: '/services', label: 'Services' },
      { to: '/saints', label: 'Saints' },
      { to: '/learning', label: 'Learn' },
      { to: '/resources', label: 'Library' },
      { to: '/watch', label: 'Watch' },
      { to: '/journey', label: 'Journey' },
      { to: '/calendar', label: 'Calendar' },
      { to: '/icons', label: 'Icons' },
    ],
  },
  {
    label: 'Community',
    items: [{ to: '/parish', label: 'Parish' }],
  },
  {
    label: 'Personal',
    items: [
      { to: '/tracking', label: 'Tracking' },
      { to: '/commemorations', label: 'Commemorations' },
      { to: '/confession', label: 'Confession' },
    ],
  },
];

const ALL_NAV = DRAWER_GROUPS.flatMap((g) => g.items);

/** Human label for the topbar, derived from the deepest matching nav entry. */
function currentSectionLabel(pathname: string): string {
  if (pathname.startsWith('/admin')) return 'Admin';
  if (pathname.startsWith('/profile')) return 'Profile';
  if (pathname.startsWith('/feasts')) return 'Feasts';
  const hit = ALL_NAV.filter(
    (it) => pathname === it.to || pathname.startsWith(`${it.to}/`),
  ).sort((a, b) => b.to.length - a.to.length)[0];
  return hit?.label ?? '';
}

export function Layout() {
  const { theme, toggle } = useTheme();
  const { user, clear } = useAuth();
  const admin = isAdmin(user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const signOut = () => {
    clear();
    navigate('/', { replace: true });
  };

  // Close the drawer whenever the route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Top-bar gains a hairline shadow once the page has scrolled past 8px.
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const section = currentSectionLabel(location.pathname);

  return (
    <div className="min-h-screen">
      {/* ── Sidebar (desktop) ─────────────────────────────────── */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-60 flex-col border-r border-hairline bg-bg">
        <NavLink
          to="/"
          className="flex items-center gap-3 px-5 h-16 border-b border-hairline text-gold shrink-0"
        >
          <Logo size={26} />
          <span className="font-display tracking-wider text-[13px] uppercase text-primary whitespace-nowrap">
            Orthodox Way
          </span>
        </NavLink>

        <nav aria-label="Primary" className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
          {DRAWER_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="px-2 font-display uppercase text-[9px] tracking-eyebrow text-ink-faint">
                {group.label}
              </p>
              <ul className="mt-2 space-y-0.5">
                {group.items.map((it) => (
                  <li key={it.to}>
                    <NavLink
                      to={it.to}
                      className={({ isActive }) =>
                        `flex items-center px-2 py-1.5 rounded-button font-display uppercase text-[11px] tracking-wider motion-safe:transition-colors ${
                          isActive
                            ? 'text-gold bg-(--ow-gold-glow) border-l-2 border-gold pl-2.5'
                            : 'text-ink-muted hover:text-gold border-l-2 border-transparent pl-2.5'
                        }`
                      }
                    >
                      {it.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

      </aside>

      {/* ── Content column ────────────────────────────────────── */}
      <div className="lg:pl-60 min-h-screen flex flex-col">
        <header
          className={`sticky top-0 z-30 backdrop-blur-sm bg-bg/85 border-b border-hairline motion-safe:transition-shadow motion-safe:duration-300 ${
            scrolled ? 'shadow-parchment' : ''
          }`}
        >
          <div className="w-full px-5 md:px-8 lg:px-10 h-16 flex items-center justify-between gap-4">
            {/* Mobile: brand mark. Desktop: current section. */}
            <NavLink to="/" className="lg:hidden flex items-center gap-3 text-gold shrink-0">
              <Logo size={28} />
              <span className="font-display tracking-wider text-sm uppercase text-primary whitespace-nowrap hidden sm:inline">
                Orthodox Way
              </span>
            </NavLink>
            <p
              className="hidden lg:block font-display uppercase text-[11px] tracking-eyebrow text-ink-muted"
              aria-current="page"
            >
              {section}
            </p>

            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={toggle}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                className="hidden lg:inline-flex w-9 h-9 items-center justify-center rounded-pill border border-hairline text-ink-muted hover:text-gold hover:border-gold motion-safe:transition-colors"
                title={theme === 'light' ? 'Light' : 'Dark'}
              >
                {theme === 'light' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
              </button>
              {user ? (
                <ProfileMenu user={user} admin={admin} onSignOut={signOut} />
              ) : (
                <NavLink
                  to="/login"
                  className="hidden lg:inline-flex btn-secondary text-[11px] whitespace-nowrap"
                >
                  Sign in
                </NavLink>
              )}
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
                aria-expanded={drawerOpen}
                aria-controls="app-drawer"
                className="lg:hidden w-11 h-11 inline-flex items-center justify-center rounded-pill border border-hairline text-ink-muted hover:text-gold hover:border-gold motion-safe:transition-colors"
              >
                <MenuIcon size={22} />
              </button>
            </div>
          </div>
        </header>

        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          groups={DRAWER_GROUPS}
          theme={theme}
          onToggleTheme={toggle}
          user={
            user
              ? {
                  name: user.first_name,
                  isAdmin: admin,
                  onSignOut: signOut,
                }
              : undefined
          }
        />

        <main className="flex-1 w-full px-5 md:px-8 lg:px-10 py-10 md:py-16">
          <Outlet />
        </main>
        <footer className="border-t border-hairline py-10">
          <div className="w-full px-5 md:px-8 lg:px-10 flex flex-col items-center text-center gap-3 text-ink-muted">
            <Logo size={36} className="text-gold opacity-70" />
            <p className="scripture text-base max-w-prose">“Glory to God for all things.”</p>
            <p className="font-display uppercase text-[10px] tracking-eyebrow text-ink-faint">
              Orthodox Way · MMXXVI
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

function initials(user: CurrentUser): string {
  const monogram = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();
  return monogram || (user.email[0] ?? '?').toUpperCase();
}

/**
 * Topbar account control: monogram avatar + name opening a small menu with
 * Settings, Help, Admin (when applicable), and Sign out. Closes on outside
 * click, Escape, and route change.
 */
function ProfileMenu({
  user,
  admin,
  onSignOut,
}: {
  user: CurrentUser;
  admin: boolean;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const itemClass =
    'flex w-full items-center px-4 py-2 text-[13px] text-ink-muted hover:text-gold hover:bg-bg-subtle motion-safe:transition-colors';

  return (
    <div ref={ref} className="relative hidden lg:block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="flex items-center gap-2.5 rounded-pill border border-hairline pl-1.5 pr-3 py-1.5 text-ink-muted hover:text-gold hover:border-gold motion-safe:transition-colors"
      >
        <span
          aria-hidden
          className="w-7 h-7 rounded-full border border-gold text-gold font-display text-[11px] tracking-wider inline-flex items-center justify-center"
        >
          {initials(user)}
        </span>
        <span className="text-xs whitespace-nowrap max-w-32 truncate">{user.first_name}</span>
        <ChevronDownIcon
          size={12}
          className={`motion-safe:transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Account"
          className="absolute right-0 top-full mt-2 w-60 bg-bg-elevated border border-hairline rounded-card shadow-lg py-2 z-50"
        >
          <div className="px-4 py-2 border-b border-hairline mb-1">
            <p className="text-sm text-primary truncate">
              {user.first_name} {user.last_name}
            </p>
            <p className="text-xs text-ink-faint truncate">{user.email}</p>
          </div>
          <NavLink role="menuitem" to="/profile" className={itemClass}>
            Settings
          </NavLink>
          {admin && (
            <NavLink role="menuitem" to="/admin" className={itemClass}>
              Admin
            </NavLink>
          )}
          <NavLink role="menuitem" to="/contact" className={itemClass}>
            Help
          </NavLink>
          <div className="border-t border-hairline mt-1 pt-1">
            <button type="button" role="menuitem" onClick={onSignOut} className={itemClass}>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
